import { useEffect, useRef, useState } from 'react';
import moment from 'moment';
import { ValColumnEnum } from '@shared-app/lib/types';
import { useFiles } from '@entities/solution';
import { getDataFromLocalStorage, trimAndLowerText } from '@shared-app/utils';
import { ToastService } from '@shared/lib/services/toast.service';
import { extractData } from '../../utils/extractData';
import { extractDate } from '../../utils/extractDate';
// Namespaced so Validation filters don't collide with other pages and so previously-persisted
// bare keys (e.g. a stale `status` selection that hid "uploaded") are ignored on first load.
const storageKey = (field) => `val:${field}`;
export function useFilteredData() {
    const { data, isLoading, isError } = useFiles();
    const projects = data || [];
    const [selectedDate, setSelectedDate] = useState([]);
    const [selectedCountry, setSelectedCountry] = useState([]);
    const [selectedSystem, setSelectedSystem] = useState([]);
    const [selectedTypes, setSelectedTypes] = useState([]);
    const [selectedLocation, setSelectedLocation] = useState([]);
    const [selectedProjectCategory, setSelectedProjectCategory] = useState([]);
    const [customerQuery, setCustomerQuery] = useState('');
    const [fileNameQuery, setFileNameQuery] = useState('');
    const [selectedStatus, setSelectedStatus] = useState([]);
    const datesArray = extractDate(projects);
    const systemArray = extractData(projects, ValColumnEnum.systems);
    const typesArray = extractData(projects, ValColumnEnum.types);
    const countryArray = extractData(projects, ValColumnEnum.office_country);
    const locationArray = extractData(projects, ValColumnEnum.office_location);
    const statusArray = extractData(projects, ValColumnEnum.status);
    const projectCategoryArray = extractData(projects, ValColumnEnum.project_type_1);
    // Seed selected filters from localStorage (falling back to "all") once files have loaded.
    const initialized = useRef(false);
    useEffect(() => {
        if (isLoading || initialized.current)
            return;
        initialized.current = true;
        setSelectedDate(getDataFromLocalStorage(storageKey(ValColumnEnum.date), datesArray));
        setSelectedSystem(getDataFromLocalStorage(storageKey(ValColumnEnum.systems), systemArray));
        setSelectedTypes(getDataFromLocalStorage(storageKey(ValColumnEnum.types), typesArray));
        setSelectedCountry(getDataFromLocalStorage(storageKey(ValColumnEnum.office_country), countryArray));
        setSelectedLocation(getDataFromLocalStorage(storageKey(ValColumnEnum.office_location), locationArray));
        setCustomerQuery(getDataFromLocalStorage(storageKey(ValColumnEnum.customer_name), ''));
        setFileNameQuery(getDataFromLocalStorage(storageKey(ValColumnEnum.drawing_file_name), ''));
        setSelectedProjectCategory(getDataFromLocalStorage(storageKey(ValColumnEnum.project_type_1), projectCategoryArray));
        // Status defaults to all present statuses; reconcile any stored selection against them.
        const storedStatus = getDataFromLocalStorage(storageKey(ValColumnEnum.status), statusArray);
        const reconciled = storedStatus.filter(status => statusArray.includes(status));
        setSelectedStatus(reconciled.length ? reconciled : statusArray);
    }, [isLoading]);
    useEffect(() => {
        if (isError)
            ToastService.error('Failed to load files');
    }, [isError]);
    const handleDataChange = (selected, field) => {
        switch (field) {
            case ValColumnEnum.date:
                setSelectedDate(selected);
                break;
            case ValColumnEnum.office_country:
                setSelectedCountry(selected);
                break;
            case ValColumnEnum.systems:
                setSelectedSystem(selected);
                break;
            case ValColumnEnum.types:
                setSelectedTypes(selected);
                break;
            case ValColumnEnum.status:
                setSelectedStatus(selected);
                break;
            case ValColumnEnum.office_location:
                setSelectedLocation(selected);
                break;
            case ValColumnEnum.project_type_1:
                setSelectedProjectCategory(selected);
                break;
            default:
                break;
        }
        localStorage.setItem(storageKey(field), JSON.stringify(selected));
    };
    const handleCustomerChange = (query) => {
        setCustomerQuery(query);
        localStorage.setItem(storageKey(ValColumnEnum.customer_name), JSON.stringify(query));
    };
    const handleFileNameChange = (query) => {
        setFileNameQuery(query);
        localStorage.setItem(storageKey(ValColumnEnum.drawing_file_name), JSON.stringify(query));
    };
    const handleResetAll = () => {
        setSelectedDate(datesArray);
        setSelectedCountry(countryArray);
        setSelectedSystem(systemArray);
        setSelectedTypes(typesArray);
        setSelectedLocation(locationArray);
        setSelectedStatus(statusArray);
        setCustomerQuery('');
        setFileNameQuery('');
        setSelectedProjectCategory(projectCategoryArray);
        [
            ValColumnEnum.date,
            ValColumnEnum.systems,
            ValColumnEnum.types,
            ValColumnEnum.office_country,
            ValColumnEnum.office_location,
            ValColumnEnum.status,
            ValColumnEnum.project_type_1,
            ValColumnEnum.customer_name,
            ValColumnEnum.drawing_file_name
        ].forEach(key => localStorage.removeItem(storageKey(key)));
    };
    const filterData = () => {
        let filtered = [...projects];
        // A filter is only applied for a strict, non-empty subset of options; when all options are
        // selected (the default "All") it is a no-op — so files with an empty field (e.g. an
        // "uploaded" file with no systems/types yet) are never hidden by default.
        const isActive = (selected, options) => selected.length > 0 && selected.length < options.length;
        if (isActive(selectedDate, datesArray)) {
            filtered = filtered.filter(row => selectedDate.includes(moment(row[ValColumnEnum.date]).format('YYYY')));
        }
        if (isActive(selectedStatus, statusArray)) {
            filtered = filtered.filter(row => selectedStatus.includes(row[ValColumnEnum.status]));
        }
        if (isActive(selectedCountry, countryArray)) {
            filtered = filtered.filter(row => selectedCountry.includes(row[ValColumnEnum.office_country]));
        }
        if (isActive(selectedSystem, systemArray)) {
            filtered = filtered.filter(row => {
                const rowSystems = row[ValColumnEnum.systems] || [];
                return selectedSystem.some(selected => rowSystems.includes(selected));
            });
        }
        if (isActive(selectedTypes, typesArray)) {
            filtered = filtered.filter(row => {
                const rowTypes = row[ValColumnEnum.types] || [];
                return selectedTypes.some(selected => rowTypes.includes(selected));
            });
        }
        if (isActive(selectedLocation, locationArray)) {
            filtered = filtered.filter(row => selectedLocation.includes(row[ValColumnEnum.office_location]));
        }
        if (isActive(selectedProjectCategory, projectCategoryArray)) {
            filtered = filtered.filter(row => selectedProjectCategory.includes(row[ValColumnEnum.project_type_1]));
        }
        const trimmedCustomerQuery = trimAndLowerText(customerQuery);
        if (trimmedCustomerQuery.length) {
            filtered = filtered.filter(row => trimAndLowerText(row[ValColumnEnum.customer_name]).includes(trimmedCustomerQuery));
        }
        const trimmedFileName = trimAndLowerText(fileNameQuery);
        if (trimmedFileName.length) {
            filtered = filtered.filter(row => trimAndLowerText(row[ValColumnEnum.drawing_file_name]).includes(trimmedFileName));
        }
        return filtered;
    };
    return {
        handleDataChange,
        handleCustomerChange,
        handleFileNameChange,
        fileNameQuery,
        filteredData: filterData(),
        isLoading,
        handleResetAll,
        datesArray,
        countryArray,
        locationArray,
        statusArray,
        projectCategoryArray,
        selectedDate,
        selectedSystem,
        selectedTypes,
        systemArray,
        typesArray,
        selectedCountry,
        selectedLocation,
        selectedProjectCategory,
        customerQuery,
        selectedStatus
    };
}
